import { ProcurementScope } from '@/actions/dtos/tender/procurement';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface Filter {
  value: string;
  label: string;
  name?: string;
}

export interface PolygonCoordinates {
  lat: number;
  lng: number;
}

export interface NutsCode extends Filter {
  children?: NutsCode[];
  expanded?: boolean;
}

export interface CpvCode extends Filter {
  children?: CpvCode[];
  expanded?: boolean;
}

export interface AreaFilter {
  radius?: number;
  coordinates: PolygonCoordinates[];
  includeEmptyLocations: boolean;
}

export const submissionEndOptions = [
  { label: 'Alle', value: '' },
  { label: 'Frühestens in 7 Tagen', value: '7' },
  { label: 'Frühestens in 2 Wochen', value: '14' },
  { label: 'Frühestens in 1 Monat', value: '30' },
];

export const publicationBeginOptions = [
  { label: 'Alle', value: '' },
  { label: 'In den letzten 7 Tagen', value: '7' },
  { label: 'In den letzten 2 Wochen', value: '14' },
  { label: 'Im letzten Monat', value: '30' },
];

export const scopeOptions = [
  { label: 'National', value: ProcurementScope.NATIONAL },
  { label: 'EU', value: ProcurementScope.EU },
];

export interface FiltersState {
  publicationBegin?: Filter;
  submissionEnd?: Filter;
  contractor?: Filter[];
  nutsCode?: NutsCode[];
  cpvCode?: CpvCode[];
  scope?: Filter;
  area?: AreaFilter;
  minScore?: Filter;
  hasChanges: boolean;
  [key: string]: Filter | Filter[] | AreaFilter | boolean | undefined;
}

export const DEFAULT_FILTER: Filter = {
  value: '',
  label: 'Alle',
};

export const DEFAULT_ARRAY_FILTER: Filter[] = [];

export const DEFAULT_AREA_FILTER: AreaFilter = {
  coordinates: [],
  includeEmptyLocations: false,
};

const initialState: FiltersState = {
  publicationBegin: { ...DEFAULT_FILTER, name: 'publicationBeginSinceDays' },
  submissionEnd: { ...DEFAULT_FILTER, name: 'submissionEndInDays' },
  contractor: DEFAULT_ARRAY_FILTER,
  nutsCode: DEFAULT_ARRAY_FILTER,
  cpvCode: DEFAULT_ARRAY_FILTER,
  scope: { ...DEFAULT_FILTER, name: 'scope' },
  area: undefined,
  minScore: { value: '10', name: 'minScore', label: 'minScore' },
  hasChanges: false,
};

const filtersSlice = createSlice({
  name: 'filters',
  initialState,
  reducers: {
    setPublicationBegin: (state, action: PayloadAction<Filter>) => {
      state.publicationBegin = { ...action.payload, name: 'publicationBeginSinceDays' };
    },
    setSubmissionEnd: (state, action: PayloadAction<Filter>) => {
      state.submissionEnd = { ...action.payload, name: 'submissionEndInDays' };
    },
    setContractor: (state, action: PayloadAction<Filter[]>) => {
      state.contractor = action.payload.map((contractor) => {
        return { ...contractor, name: 'contractor' };
      });
    },
    setNutsCode: (state, action: PayloadAction<NutsCode[]>) => {
      state.nutsCode = action.payload.map((contractor) => {
        return { ...contractor, name: 'nutsCode' };
      });
    },
    setCpvCode: (state, action: PayloadAction<CpvCode[]>) => {
      state.cpvCode = action.payload.map((code) => {
        return { ...code, name: 'cpvCode' };
      });
    },
    setScope: (state, action: PayloadAction<Filter>) => {
      state.scope = { ...action.payload, name: 'scope' };
    },
    setArea: (state, action: PayloadAction<AreaFilter>) => {
      state.area = { ...action.payload };
    },
    setMinScore: (state, action: PayloadAction<{ value: string }>) => {
      state.minScore = { ...action.payload, name: 'minScore', label: 'minScore' };
    },
    removeFilter: (state, action: PayloadAction<string>) => {
      const key = action.payload as keyof FiltersState;

      switch (key) {
        case 'minScore':
          state.minScore = initialState.minScore;
          break;
        case 'publicationBegin':
          state.publicationBegin = initialState.publicationBegin;
          break;
        case 'submissionEnd':
          state.submissionEnd = initialState.submissionEnd;
          break;
        case 'contractor':
          state.contractor = initialState.contractor;
          break;
        case 'nutsCode':
          state.nutsCode = initialState.nutsCode;
          break;
        case 'cpvCode':
          state.cpvCode = initialState.cpvCode;
          break;
        case 'scope':
          state.scope = initialState.scope;
          break;
        case 'area':
          state.area = undefined;
          break;
      }
    },
    resetFilters: () => {
      return { ...initialState };
    },
    setHasChanges: (state, action: PayloadAction<boolean>) => {
      state.hasChanges = action.payload;
    },
  },
});

export const {
  setNutsCode,
  setPublicationBegin,
  setSubmissionEnd,
  setContractor,
  setCpvCode,
  setScope,
  setArea,
  setMinScore,
  removeFilter,
  resetFilters,
  setHasChanges,
} = filtersSlice.actions;

export default filtersSlice.reducer;
